import { type ActionType, type SyncedActionType } from '@soulhx/fs-common';
import { DateTime } from 'luxon';
import { v4 as uuid } from 'uuid';
import { db, type ItemSyncStatus, type LocalActionType } from '../model/db.js';
import { triggerGlobalSync } from './GlobalSyncMiddleware.js';
import { syncFetch, syncPull } from './syncFetch.js';
import { nextSyncCursor, pullSince, toUtcIso } from './timestamps.js';

export const ACTIONTYPE_SYNC_POLL_INTERVAL_MS = 20_000;
export const ACTIONTYPE_SYNC_KEY = 'actionTypes';

const ACTIONTYPES_PATH = '/do/t';

// see SettingSync.ts - passing `since` at all (even this far back) routes
// the server to the sync-oriented delta endpoint on the very first pull
const EPOCH = new Date(0).toISOString();

const isUnsynced = (status: ItemSyncStatus | undefined): boolean => status !== undefined && status !== 'synced';

const logSyncError = (context: string, error: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`action type sync: ${context}`, error);
};

// `POST /do/t` doesn't accept a client-supplied id for a brand-new type
// (unlike passages) - it always mints its own uuid. So a locally-created
// type's placeholder id is always replaced by the server's canonical id
// here, and any entry already logged against the placeholder (a narrow
// race - creating a type and immediately logging against it before the
// create has pushed) gets repointed onto the canonical id.
const pushCreate = async (local: LocalActionType, keepalive?: boolean): Promise<void> => {
  const body: ActionType = {
    displayName: local.displayName,
    isDefault: local.isDefault,
    isAvailable: local.isAvailable,
  };
  const saved = await syncFetch<ActionType>({ method: 'POST', path: `${ACTIONTYPES_PATH}/`, body, keepalive });

  await db.transaction('rw', db.actionTypes, db.actionEntries, async () => {
    await db.actionTypes.delete(local.id);
    const toStore: LocalActionType = {
      id: saved.id!,
      displayName: saved.displayName,
      isDefault: saved.isDefault,
      isAvailable: saved.isAvailable,
      updateDate: DateTime.now().toISO(),
      syncStatus: 'synced',
    };
    await db.actionTypes.put(toStore);

    const referencing = await db.actionEntries.filter((e) => e.actionType === local.id).toArray();
    for (const entry of referencing) {
      await db.actionEntries.update(entry.id, { actionType: saved.id! });
    }
  });
};

const pushUpdate = async (local: LocalActionType, keepalive?: boolean): Promise<void> => {
  const body: ActionType = {
    id: local.id,
    displayName: local.displayName,
    isDefault: local.isDefault,
    isAvailable: local.isAvailable,
  };
  const saved = await syncFetch<ActionType>({ method: 'POST', path: `${ACTIONTYPES_PATH}/`, body, keepalive });

  await db.actionTypes.update(local.id, {
    displayName: saved.displayName,
    isAvailable: saved.isAvailable,
    updateDate: DateTime.now().toISO(),
    syncStatus: 'synced',
  });
};

const pushDelete = async (local: LocalActionType, keepalive?: boolean): Promise<void> => {
  await syncFetch<{ success: boolean }>({ method: 'DELETE', path: `${ACTIONTYPES_PATH}/${local.id}`, keepalive });
  await db.actionTypes.delete(local.id);
};

export const pushPendingActionTypes = async (options?: { keepalive?: boolean }): Promise<void> => {
  const keepalive = options?.keepalive;
  const pending = await db.actionTypes.filter((t) => isUnsynced(t.syncStatus)).toArray();

  for (const local of pending) {
    try {
      switch (local.syncStatus ?? 'pendingCreate') {
        case 'pendingCreate':
          await pushCreate(local, keepalive);
          break;
        case 'pendingUpdate':
          await pushUpdate(local, keepalive);
          break;
        case 'pendingDelete':
          await pushDelete(local, keepalive);
          break;
      }
    } catch (error) {
      // leave the row's syncStatus untouched so it's retried on the next cycle
      logSyncError(`failed to push action type ${local.id}`, error);
    }
  }
};

export const pullActionTypes = async (): Promise<void> => {
  const cursor = await db.syncMeta.get(ACTIONTYPE_SYNC_KEY);
  const since = cursor?.lastSyncedAt ?? EPOCH;
  const { data: serverTypes, horizon } = await syncPull<SyncedActionType[]>(
    `${ACTIONTYPES_PATH}/sync?since=${encodeURIComponent(pullSince(since))}`
  );

  let maxLastUpdate = toUtcIso(since);

  await db.transaction('rw', db.actionTypes, db.syncMeta, async () => {
    for (const serverType of serverTypes) {
      // compared in UTC: the API emits its own zone, and the stored cursor
      // may be a (UTC) sync horizon - see nextSyncCursor
      const updateDate = toUtcIso(serverType.updateDate);
      if (updateDate > maxLastUpdate) {
        maxLastUpdate = updateDate;
      }

      if (serverType.deleted) {
        await db.actionTypes.delete(serverType.id!);
        continue;
      }

      const local = await db.actionTypes.get(serverType.id!);
      if (local && isUnsynced(local.syncStatus)) continue;

      if (local?.updateDate === serverType.updateDate) continue;

      const toStore: LocalActionType = { ...serverType, id: serverType.id!, syncStatus: 'synced' };
      await db.actionTypes.put(toStore);
    }

    await db.syncMeta.put({ itemId: ACTIONTYPE_SYNC_KEY, lastSyncedAt: nextSyncCursor(since, maxLastUpdate, horizon) });
  });
};

/**
 * Pushes any pending local action-type mutations, then pulls whatever
 * changed on the server since the last sync. Push-before-pull so local
 * changes reach `synced` before we ask "what's new", minimizing cases
 * where the pull side has to skip an in-flight local row.
 */
export const syncActionTypes = async (): Promise<void> => {
  await pushPendingActionTypes();
  await pullActionTypes();
};

export const queueLocalActionTypeCreate = async (displayName: string, isAvailable = true): Promise<string> => {
  const localType: LocalActionType = {
    id: uuid(),
    displayName,
    isDefault: false,
    isAvailable,
    updateDate: DateTime.now().toISO(),
    syncStatus: 'pendingCreate',
  };

  await db.actionTypes.add(localType);
  triggerGlobalSync(ACTIONTYPE_SYNC_KEY);
  return localType.id;
};

/**
 * Also the way to "track"/"untrack" an existing type - `isAvailable` is
 * pushed through the same `POST /do/t` call that updates the type itself,
 * since the server has never had a separate write endpoint for user_action.
 */
export const queueLocalActionTypeUpdate = async (
  id: string,
  changes: { displayName?: string; isAvailable?: boolean }
): Promise<void> => {
  const updated = await db.transaction('rw', db.actionTypes, async () => {
    const existing = await db.actionTypes.get(id);
    if (!existing || existing.syncStatus === 'pendingDelete') return false;

    const nextStatus: ItemSyncStatus = existing.syncStatus === 'pendingCreate' ? 'pendingCreate' : 'pendingUpdate';

    await db.actionTypes.update(id, {
      displayName: changes.displayName ?? existing.displayName,
      isAvailable: changes.isAvailable ?? existing.isAvailable,
      updateDate: DateTime.now().toISO(),
      syncStatus: nextStatus,
    });
    return true;
  });

  if (updated) triggerGlobalSync(ACTIONTYPE_SYNC_KEY);
};

export const queueLocalActionTypeDelete = async (id: string): Promise<void> => {
  const shouldSync = await db.transaction('rw', db.actionTypes, async () => {
    const existing = await db.actionTypes.get(id);
    if (!existing) return false;

    if (existing.syncStatus === undefined || existing.syncStatus === 'pendingCreate') {
      // never reached the server - just vanish, no network call
      await db.actionTypes.delete(id);
      return false;
    }

    if (existing.syncStatus === 'pendingDelete') return false;

    await db.actionTypes.update(id, { syncStatus: 'pendingDelete' });
    return true;
  });

  if (shouldSync) triggerGlobalSync(ACTIONTYPE_SYNC_KEY);
};
